import { DotLegalAlert, DotLegalDialog, DotLegalHeader, DotLegalSelect, DotLegalTextField } from "@dotlegal/dotlegal-ui-components";
import { useTranslation } from "@localization/useTranslation";
import { useAddLegalEntityDialogHook } from "./AddLegalEntityDialog.hooks";
import { LegalEntityScope } from "../legalEntities/LegalEntities.types";
import CvrSelector from "../cvrSelector/CvrSelector";
import CvrSelectorResult from "../cvrSelector/CvrSelectorResult";
import { Box, Collapse } from "@mui/material";
import useCountryHook from "@common/hooks/useCountryList";
import { LegalEntityCustomFieldRenderer, LegalEntityStandardFieldRenderer } from "../../entityTemplates/LegalEntityFieldRenderers";
import { LegalEntityStandardField } from "../../entityTemplates/EntityTemplate.types";
import TagsMultiSelect from "@common/components/tagsMultiSelect/TagsMultiSelect";
import { TagRelationType } from "@common/components/tagsMultiSelect/TagsMultiSelect.types";
import SelectableLegalEntityMultiSelect from "../SelectableLegalEntityMultiSelect";

export interface IAddLegalEntityDialog {
    onDialogClose: () => void;
    // Creation from one of the two lists or the top bar: the scope decides ownership and where creation lands.
    legalEntityScope?: LegalEntityScope;
    // Creation from inside another flow: the caller takes the new id and keeps the user where they are.
    onEntityCreated?: (id: string) => void;
    onCreatedAndRedirect?: (id: string, legalEntityScope: LegalEntityScope) => void;
}

function AddLegalEntityDialog(props: IAddLegalEntityDialog) {
    const { translateString } = useTranslation();
    const {
        saveModel,
        onSaveModelChange,
        onSaveClick,
        onSaveAndRedirectClick,
        canCreateAndRedirect,
        okButtonTranslationKey,
        validation,
        isSaving,
        onCvrLegalEntitySelected,
        onCvrLegalEntityRemoved,
        selectedCvrResult,
        templates,
        templatesIsLoading,
        onTemplateChange,
        selectedTemplateMissingAccess,
        hasStandardRequiredFields,
        standardRequiredFields,
        customRequiredFields,
        tagsRequiredAtCreation,
        subContractorsRequiredAtCreation,
        selectedTagIds,
        setSelectedTagIds,
        editedLegalEntity,
        disableFields,
        onRequiredFieldChange,
        requiredFieldValidationErrors,
    } = useAddLegalEntityDialogHook(props);
    const { selectableCountries, countriesIsLoading } = useCountryHook();

    const getErrorText = (standardField: LegalEntityStandardField) =>
        requiredFieldValidationErrors.find((v) => v.field === LegalEntityStandardField[standardField])?.error;

    return (
        <DotLegalDialog
            size="sm"
            buttonOkText={translateString(okButtonTranslationKey)}
            open
            onDialogClose={props.onDialogClose}
            onOkClick={onSaveClick}
            previousButtonText={canCreateAndRedirect ? translateString("fullCreate") : undefined}
            previousButtonClick={canCreateAndRedirect ? onSaveAndRedirectClick : undefined}
            previousButtonStyle="secondary"
            disablePreviousButton={isSaving || selectedTemplateMissingAccess}
            header={translateString("createLegalEntity")}
            inProgress={isSaving}
            disableBackdropOnCloseIfValueChange={saveModel}
            okButtonDisabled={selectedTemplateMissingAccess}
        >
            <Box marginRight={3}>
                <DotLegalTextField
                    debounce={false}
                    size="small"
                    label={translateString("name")}
                    value={saveModel.name}
                    onChange={onSaveModelChange.onNameChange}
                    errorText={validation?.getErrorsForDisplay("name")}
                    autoFocus
                    disabled={disableFields}
                />
                <DotLegalSelect
                    isLoading={countriesIsLoading}
                    label={translateString("country")}
                    selectedItem={saveModel.countryCode === "" ? null : saveModel.countryCode}
                    onChange={(countryCode) => onSaveModelChange.onCountryChange(countryCode ?? "")}
                    options={selectableCountries}
                    placeholder={translateString("country")}
                    errorText={validation?.getErrorsForDisplay("countryCode")}
                    noOptionsLabel={translateString("noOptions")}
                    disableClearable
                    disabled={disableFields}
                />

                <DotLegalSelect
                    options={templates}
                    onChange={onTemplateChange}
                    label={translateString("legalEntityTemplate")}
                    noOptionsLabel={translateString("noOptions")}
                    isLoading={templatesIsLoading}
                    toolTipText={translateString("addLegalEntityTemplateTooltip")}
                    disableClearable
                    selectedItem={saveModel.entityTemplateId === "" ? null : saveModel.entityTemplateId}
                    errorText={validation?.getErrorsForDisplay("entityTemplateId")}
                />

                {selectedTemplateMissingAccess && (
                    <Box sx={{ marginTop: 2 }}>
                        <DotLegalAlert severity="info" warningText={translateString("missingRequiredAtCreationFieldAccess")} />
                    </Box>
                )}

                <Collapse in={!selectedTemplateMissingAccess && (hasStandardRequiredFields || customRequiredFields.length > 0)}>
                    <>
                        {hasStandardRequiredFields && (
                            <>
                                <DotLegalHeader headerStyle="small">{translateString("standardFields")}</DotLegalHeader>
                                {standardRequiredFields.map((field) => (
                                    <Box key={field.id} sx={{ marginBottom: 2 }}>
                                        <LegalEntityStandardFieldRenderer
                                            field={field}
                                            legalEntity={editedLegalEntity}
                                            legalEntityId=""
                                            onChange={onRequiredFieldChange}
                                            validationErrors={requiredFieldValidationErrors}
                                            isLoading={false}
                                            isPreview={false}
                                        />
                                    </Box>
                                ))}
                                {tagsRequiredAtCreation && (
                                    <TagsMultiSelect
                                        selectedTags={selectedTagIds}
                                        onChange={setSelectedTagIds}
                                        entityType={TagRelationType.LegalEntity}
                                        isLoading={false}
                                        errorText={getErrorText(LegalEntityStandardField.LegalEntityTags)}
                                    />
                                )}
                                {subContractorsRequiredAtCreation && (
                                    <SelectableLegalEntityMultiSelect
                                        selectedIds={editedLegalEntity.subContractorIds}
                                        onChange={(subContractorIds) =>
                                            onRequiredFieldChange((legalEntity) => (legalEntity.subContractorIds = subContractorIds))
                                        }
                                        label={translateString("subContractors")}
                                        errorText={getErrorText(LegalEntityStandardField.LegalEntitySubContractors)}
                                    />
                                )}
                            </>
                        )}
                        {customRequiredFields.length > 0 && (
                            <>
                                <DotLegalHeader headerStyle="small">{translateString("customFields")}</DotLegalHeader>
                                {customRequiredFields.map((field) => (
                                    <Box key={field.id} sx={{ marginBottom: 2 }}>
                                        <LegalEntityCustomFieldRenderer
                                            field={field}
                                            legalEntity={editedLegalEntity}
                                            legalEntityId=""
                                            onChange={onRequiredFieldChange}
                                            validationErrors={requiredFieldValidationErrors}
                                            isLoading={false}
                                            isPreview={false}
                                        />
                                    </Box>
                                ))}
                            </>
                        )}
                    </>
                </Collapse>

                <CvrSelector onSelectLegalEntity={onCvrLegalEntitySelected} onLegalEntityRemoved={onCvrLegalEntityRemoved} />
                <Box sx={{ mt: 2 }}>
                    <CvrSelectorResult clearSelectedResult={onCvrLegalEntityRemoved} selectedResult={selectedCvrResult} />
                </Box>
            </Box>
        </DotLegalDialog>
    );
}

export default AddLegalEntityDialog;
